import { defineStore } from 'pinia';
import { ref, onMounted, onUnmounted } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';

export interface Toast {
  id: number;
  variant: 'success' | 'error';
  message: string;
}

let _toastSeq = 0;

export const useSourcesStore = defineStore('sources', () => {
  const modalOpen = ref(false);
  const toasts = ref<Toast[]>([]);

  function openModal(): void {
    modalOpen.value = true;
  }

  function closeModal(): void {
    modalOpen.value = false;
  }

  function addToast(variant: Toast['variant'], message: string): void {
    const id = ++_toastSeq;
    toasts.value.push({ id, variant, message });
    setTimeout(() => {
      const idx = toasts.value.findIndex(t => t.id === id);
      if (idx !== -1) toasts.value.splice(idx, 1);
    }, 4000);
  }

  function dismissToast(id: number): void {
    const idx = toasts.value.findIndex(t => t.id === id);
    if (idx !== -1) toasts.value.splice(idx, 1);
  }

  // Every connect path (MS Graph popup, Freshdesk form) funnels here: refetch
  // sources/draft/events. The rerun check reports the new source's events as
  // drift, and the drift banner offers the update (ADR-0031).
  function noteSourceConnected(queryClient: ReturnType<typeof useQueryClient>): void {
    void queryClient.invalidateQueries({ queryKey: ['sources'] });
    void queryClient.invalidateQueries({ queryKey: ['draft'] });
    void queryClient.invalidateQueries({ queryKey: ['draft-check'] });
    void queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
  }

  // postMessage listener — installed once in App.vue; verifies same-origin
  // before acting (ADR-0029 §6).
  function makeMessageListener(queryClient: ReturnType<typeof useQueryClient>) {
    return function handleMessage(event: MessageEvent): void {
      if (event.origin !== window.location.origin) return;

      const data = event.data as { type?: string; reason?: string } | null;
      if (!data || typeof data.type !== 'string') return;

      const linked = (successMessage: string): void => {
        noteSourceConnected(queryClient);
        addToast('success', successMessage);
      };
      const failed = (reason: string | undefined, fallback: string): void => {
        void queryClient.invalidateQueries({ queryKey: ['sources'] });
        addToast('error', typeof reason === 'string' && reason ? reason : fallback);
      };

      if (data.type === 'msgraph_linked') linked('Calendar connected.');
      else if (data.type === 'msgraph_error') failed(data.reason, 'Calendar connection failed.');
      else if (data.type === 'zoom_linked') linked('Zoom connected.');
      else if (data.type === 'zoom_tenant_linked') linked('Zoom set up.');
      else if (data.type === 'zoom_error') failed(data.reason, 'Zoom connection failed.');
    };
  }

  return {
    modalOpen,
    toasts,
    openModal,
    closeModal,
    addToast,
    dismissToast,
    noteSourceConnected,
    makeMessageListener,
  };
});

// Composable used in App.vue to register the listener once with a stable
// queryClient reference. Must be called inside setup().
export function useSourcesMessageListener(): void {
  const store = useSourcesStore();
  const queryClient = useQueryClient();

  let listener: ((e: MessageEvent) => void) | null = null;

  onMounted(() => {
    listener = store.makeMessageListener(queryClient);
    window.addEventListener('message', listener);
  });

  onUnmounted(() => {
    if (listener) window.removeEventListener('message', listener);
  });
}
